import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { Money } from '../shared/money';
import type {
  EmployeeId,
  ExecutionResourceId,
  ExternalServiceId,
  ProjectId,
  UnitOfMeasureId,
} from '../shared/ids';

/**
 * Recurso de execução.
 *
 * A abstração que permite alocar funcionário e serviço terceirizado pelo **mesmo**
 * mecanismo (ADR-009). Sem ela, o planejamento teria dois caminhos paralelos para a mesma
 * pergunta — "quem vai executar isto?" — e o segundo sempre ficaria para trás.
 *
 * Serviço terceirizado **não** é material (RN-056): não tem quantidade a comprar, tem
 * capacidade a alocar. Tratá-lo como insumo faria a mão de obra terceirizada aparecer no
 * pedido de compra e sumir do planejamento.
 */
export type ResourceType = 'EMPLOYEE' | 'SERVICE';

export const RESOURCE_TYPE_LABEL: Readonly<Record<ResourceType, string>> = {
  EMPLOYEE: 'Funcionário',
  SERVICE: 'Serviço terceirizado',
};

export const RESOURCE_ERROR = {
  EXCLUSIVE_ARC: 'WORKFORCE.RESOURCE_EXCLUSIVE_ARC',
  NOT_FOUND: 'WORKFORCE.RESOURCE_NOT_FOUND',
} as const;

export interface ExecutionResource {
  readonly id: ExecutionResourceId;
  readonly projectId: ProjectId;
  readonly type: ResourceType;
  /** Presente exatamente quando `type = EMPLOYEE` (RN-057). */
  readonly employeeId?: EmployeeId;
  /** Presente exatamente quando `type = SERVICE` (RN-057). */
  readonly externalServiceId?: ExternalServiceId;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateResourceInput {
  readonly id: ExecutionResourceId;
  readonly projectId: ProjectId;
  readonly employeeId?: EmployeeId;
  readonly externalServiceId?: ExternalServiceId;
  readonly now: string;
}

/**
 * Cria o recurso — **e recusa o inválido** (RN-057).
 *
 * O arco exclusivo é invariante de construção, não checagem posterior: um recurso com os
 * dois vínculos, ou com nenhum, simplesmente não existe. As outras duas camadas de
 * garantia são os índices únicos do banco e a verificação de integridade da Fase 11.
 */
export function createExecutionResource(
  input: CreateResourceInput,
): Result<ExecutionResource, DomainError> {
  const hasEmployee = input.employeeId !== undefined;
  const hasService = input.externalServiceId !== undefined;

  if (hasEmployee === hasService) {
    return err(
      domainError(
        RESOURCE_ERROR.EXCLUSIVE_ARC,
        hasEmployee
          ? 'Um recurso de execução aponta para um funcionário ou para um serviço, nunca para os dois.'
          : 'Um recurso de execução precisa apontar para um funcionário ou para um serviço.',
      ),
    );
  }

  return ok({
    id: input.id,
    projectId: input.projectId,
    type: hasEmployee ? 'EMPLOYEE' : 'SERVICE',
    isActive: true,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.employeeId === undefined ? {} : { employeeId: input.employeeId }),
    ...(input.externalServiceId === undefined
      ? {}
      : { externalServiceId: input.externalServiceId }),
  });
}

// ── Serviço terceirizado ────────────────────────────────────────────────────────

export interface ExternalService {
  readonly id: ExternalServiceId;
  readonly projectId: ProjectId;
  readonly name: string;
  /** Empresa contratada. */
  readonly company?: string;
  readonly contact?: string;
  /**
   * Capacidade diária em quartos de hora.
   *
   * Ausente = **sem limite** (RN-047): uma equipe terceirizada de doze pessoas não tem
   * "9h por dia", e inventar um teto para ela produziria conflito onde não há.
   */
  readonly dailyCapacityQuarterHours?: number;
  /**
   * Como o serviço é cobrado. Ausente nos cadastros anteriores à distinção, que o mapper
   * lê como `HOURLY` — era o único modo que existia.
   */
  readonly pricingMode: ServicePricingMode;
  /** Preço da hora. Só vale quando `pricingMode = HOURLY`. */
  readonly hourlyCost?: Money;
  /**
   * A unidade contratada — m², m³, un. Obrigatória quando `pricingMode = UNIT`: preço por
   * unidade sem dizer qual unidade é um número sem leitura possível.
   */
  readonly unitOfMeasureId?: UnitOfMeasureId;
  /** Preço de cada unidade produzida. Só vale quando `pricingMode = UNIT`. */
  readonly unitCost?: Money;
  readonly isActive: boolean;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/**
 * Como o terceirizado é contratado.
 *
 * Nem toda empreiteira vende hora. A de alvenaria costuma cobrar por m² levantado, a de
 * impermeabilização por m² aplicado — o que a obra contrata é **produção**, e as horas da
 * equipe são problema dela, não da obra.
 *
 * A distinção não é cosmética: ela muda de onde sai o custo. Por hora, o custo é
 * `horas × preço/hora`; por unidade, é `quantidade × preço/unidade`, e a quantidade vem da
 * meta e da medição da conferência. Guardar só um "custo/hora" para quem cobra por m²
 * obrigaria alguém a inventar um divisor, e o número inventado atravessaria o painel
 * inteiro parecendo medido.
 */
export type ServicePricingMode = 'HOURLY' | 'UNIT';

export const SERVICE_PRICING_LABEL: Readonly<Record<ServicePricingMode, string>> = {
  HOURLY: 'Por hora trabalhada',
  UNIT: 'Por unidade produzida',
};

export const SERVICE_ERROR = {
  NAME_REQUIRED: 'WORKFORCE.SERVICE_NAME_REQUIRED',
  NAME_TAKEN: 'WORKFORCE.SERVICE_NAME_TAKEN',
  NOT_FOUND: 'WORKFORCE.SERVICE_NOT_FOUND',
  UNIT_REQUIRED: 'WORKFORCE.SERVICE_UNIT_REQUIRED',
} as const;

export function createExternalService(input: {
  id: ExternalServiceId;
  projectId: ProjectId;
  name: string;
  company?: string;
  contact?: string;
  dailyCapacityQuarterHours?: number;
  pricingMode?: ServicePricingMode;
  hourlyCost?: Money;
  unitOfMeasureId?: UnitOfMeasureId;
  unitCost?: Money;
  notes?: string;
  now: string;
}): Result<ExternalService, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(domainError(SERVICE_ERROR.NAME_REQUIRED, 'O nome do serviço é obrigatório.'));
  }

  const pricingMode = input.pricingMode ?? 'HOURLY';

  // Quem cobra por unidade precisa dizer **qual**. Sem isso o preço não tem leitura, e o
  // custo da obra sairia somando reais por número solto.
  if (pricingMode === 'UNIT' && input.unitOfMeasureId === undefined) {
    return err(
      domainError(
        SERVICE_ERROR.UNIT_REQUIRED,
        'Escolha a unidade de medição do serviço contratado por unidade.',
      ),
    );
  }

  /*
   * O preço do modo que não vale é **descartado**, não guardado "por via das dúvidas".
   * Um custo/hora esquecido num serviço cobrado por m² é exatamente o tipo de número que
   * volta a aparecer num relatório seis meses depois, sem ninguém saber de onde veio.
   */
  return ok({
    id: input.id,
    projectId: input.projectId,
    name,
    pricingMode,
    isActive: true,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.company === undefined ? {} : { company: input.company }),
    ...(input.contact === undefined ? {} : { contact: input.contact }),
    ...(input.dailyCapacityQuarterHours === undefined
      ? {}
      : { dailyCapacityQuarterHours: input.dailyCapacityQuarterHours }),
    ...(pricingMode === 'HOURLY' && input.hourlyCost !== undefined
      ? { hourlyCost: input.hourlyCost }
      : {}),
    ...(pricingMode === 'UNIT' && input.unitOfMeasureId !== undefined
      ? { unitOfMeasureId: input.unitOfMeasureId }
      : {}),
    ...(pricingMode === 'UNIT' && input.unitCost !== undefined ? { unitCost: input.unitCost } : {}),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

export function serviceNaturalKey(projectId: ProjectId, name: string): string {
  return `${projectId}|${normalizeName(name)}`;
}

export function compareServices(a: ExternalService, b: ExternalService): number {
  return compareNames(a.name, b.name);
}

/**
 * O serviço tem preço utilizável? (RN-055)
 *
 * "Sem preço" e "custa zero" são coisas diferentes, e o painel precisa saber distinguir:
 * o total de custo com um serviço sem preço é **parcial**, não menor.
 */
export function hasUsablePrice(service: ExternalService): boolean {
  return service.pricingMode === 'UNIT'
    ? service.unitCost !== undefined && service.unitOfMeasureId !== undefined
    : service.hourlyCost !== undefined;
}

/** Serviço sem capacidade declarada é ilimitado — não gera conflito (RN-047). */
export function hasDeclaredCapacity(service: ExternalService): boolean {
  return service.dailyCapacityQuarterHours !== undefined;
}
